import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Wand2, Package, Sparkles } from "lucide-react";

type PanelImage = { scene_number?: number; storyboard_index?: number; image_url?: string; edit_status?: string; edit_error?: string | null; upscaled?: boolean; upscale_status?: string | null };
type SceneVideo = { scene_number?: number; status?: string; error?: string };

/**
 * Renders the individual extracted/upscaled storyboard panels (in scene order)
 * as a clickable grid inside the Storyboards drawer. Clicking a panel opens an
 * enlarged view with an edit field; submitting sends that panel image directly
 * to the highest-quality image model (KIE Nano Banana 2 @ 4K) via the dedicated
 * panel-edit endpoint — telling it to keep everything else the same — and swaps
 * in the updated panel when it arrives (the endpoint returns 202; the new image
 * is delivered via the scripts poll). This is independent of scene video edits.
 *
 * Renders nothing when no panels have been extracted yet, so the full storyboard
 * grid thumbnails still show on their own.
 */
export function StoryboardPanelGrid({ script, campaignId, variant = "ugc" }: { script: any; campaignId: string; variant?: "ugc" | "claymation" | "videoExpress" | "pixar" | "simpsons" | "dayinlife" }) {
  const { toast } = useToast();
  // Shared by the UGC, Claymation, and Pixar detail pages — resource bases must
  // follow the variant so a claymation/pixar script id never hits a
  // /api/ugc-scripts/* route (which 404s "Script not found").
  const campaignResource = variant === "claymation" ? "claymation-campaigns" : variant === "videoExpress" ? "video-express-campaigns" : variant === "pixar" ? "pixar-campaigns" : variant === "simpsons" ? "simpsons-campaigns" : variant === "dayinlife" ? "dayinlife-campaigns" : "ugc-campaigns";
  const scriptResource = variant === "claymation" ? "claymation-scripts" : variant === "videoExpress" ? "video-express-scripts" : variant === "pixar" ? "pixar-scripts" : variant === "simpsons" ? "simpsons-scripts" : variant === "dayinlife" ? "dayinlife-scripts" : "ugc-scripts";
  const scriptsKey = [`/api/${campaignResource}`, campaignId, "scripts"];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: scriptsKey });

  const panels: PanelImage[] = ((script.panelImages as PanelImage[]) || [])
    .filter((p) => !!p.image_url)
    .slice()
    .sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0));

  const sceneVideos: SceneVideo[] = (script.sceneVideos as SceneVideo[]) || [];
  const videoByScene = new Map<number, SceneVideo>();
  sceneVideos.forEach((v) => { if (v.scene_number != null) videoByScene.set(v.scene_number, v); });

  // Per-scene edit instruction + a snapshot of the panel image at submit time.
  // The edit endpoint returns 202 and the regenerated image arrives via polling,
  // so a panel is "working" until its polled image_url differs from the snapshot.
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [working, setWorking] = useState<Record<number, string>>({});

  // Drop snapshots once the panel image has actually swapped in (or the edit
  // failed), keeping state tidy across re-edits.
  useEffect(() => {
    setWorking((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const p of panels) {
        const n = p.scene_number || 0;
        const failed = p.edit_status === "error" || !!p.edit_error;
        if (next[n] !== undefined && (next[n] !== p.image_url || failed)) {
          delete next[n];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [script.panelImages]);

  // Per-panel upscaling is a deliberate, user-triggered step: after storyboards
  // are generated the panels are sliced but NOT upscaled, so the pipeline pauses
  // here until the user starts upscaling. Busy state reuses the per-panel
  // edit_status="pending" set up front by the upscale pass.
  // Optionally pass an array of scene numbers to re-upscale only those panels
  // (used to retry a single failed panel without re-running the whole batch).
  // Continue-by-default: with no args the server upscales only the panels that
  // aren't done yet (skips already-upscaled). `scenes` retries specific panels;
  // `replace:true` forces a full re-upscale of every panel.
  const upscaleMutation = useMutation({
    mutationFn: async (opts?: { scenes?: number[]; replace?: boolean }) =>
      apiRequest(
        "POST",
        `/api/${scriptResource}/${script.id}/upscale-panels`,
        opts?.scenes && opts.scenes.length
          ? { scenes: opts.scenes }
          : opts?.replace
            ? { replace: true }
            : undefined,
      ),
    onSuccess: (_d, opts) => {
      invalidate();
      // The route returns 202 *before* the background worker writes
      // upscale_status:"queued", so this first refetch can land before any
      // in-flight state exists — which would make the parent's poll predicate
      // see nothing upscaling and stop. Re-invalidate shortly after so the
      // refetch catches the queued panels; from there the predicate sustains
      // polling until each upscaled image_url swaps in.
      setTimeout(invalidate, 2500);
      const scenes = opts?.scenes;
      const partial = scenes && scenes.length;
      toast({
        title: partial
          ? scenes!.length === 1
            ? `Re-upscaling panel ${scenes![0]}…`
            : `Re-upscaling ${scenes!.length} panels…`
          : "Upscaling panels…",
        description: partial
          ? "Re-running only the selected panel(s) to 4K — the rest stay as they are."
          : "Enhancing each panel to 4K. They'll update here as they finish.",
      });
    },
    onError: (e: any) => toast({ title: "Failed to start upscaling", description: e?.message, variant: "destructive" }),
  });

  const editMutation = useMutation({
    mutationFn: async ({ n, instruction, mode }: { n: number; instruction?: string; mode?: "edit" | "product" }) =>
      apiRequest("POST", `/api/${scriptResource}/${script.id}/panels/${n}/edit`, { instruction, mode }),
    onSuccess: (_d, vars) => {
      invalidate();
      if (vars.mode !== "product") setEdits((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({
        title: vars.mode === "product" ? `Re-rendering product in panel ${vars.n}…` : `Editing panel ${vars.n}…`,
        description: vars.mode === "product"
          ? "Sending the panel + product reference to the image model, then swapping in the updated panel."
          : "Sending your change to the image model, then swapping in the updated panel.",
      });
    },
    onError: (e: any, vars) => {
      setWorking((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({ title: "Failed to apply edit", description: e?.message, variant: "destructive" });
    },
  });

  if (panels.length === 0) return null;

  // Pipeline pauses after extraction: panels are sliced but not yet upscaled.
  // The upscale batch tracks per-panel progress on `upscale_status`
  // (queued → processing → done/error), so we can show exactly which panels are
  // being upscaled right now vs still waiting in line.
  const anyUpscaleActive = panels.some(
    (p) => p.upscale_status === "processing" || p.upscale_status === "queued",
  );
  const upscalingCount = panels.filter((p) => p.upscale_status === "processing").length;
  const allUpscaled = panels.every((p) => !!p.upscaled);
  const anyUpscaled = panels.some((p) => !!p.upscaled);
  // Panels whose upscale failed — surfaced individually so the user can retry
  // just those instead of re-running every panel.
  const failedScenes = panels
    .filter((p) => p.upscale_status === "error")
    .map((p) => p.scene_number || 0);
  const anyFailed = failedScenes.length > 0;
  const upscaleBusy = upscaleMutation.isPending || anyUpscaleActive;

  return (
    <div className="mt-3" data-testid={`storyboard-panels-${script.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <p className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/60">
          Panels <span className="normal-case font-normal">· click any panel to edit</span>
        </p>
        {upscaleBusy ? (
          <Button
            type="button"
            size="sm"
            className="h-6 px-2.5 text-[10px] rounded-full"
            disabled
            data-testid={`button-upscale-panels-${script.id}`}
          >
            <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Upscaling…
          </Button>
        ) : anyFailed ? (
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="h-6 px-2.5 text-[10px] rounded-full"
            onClick={() => upscaleMutation.mutate({ scenes: failedScenes })}
            data-testid={`button-retry-failed-panels-${script.id}`}
          >
            <Sparkles className="h-3 w-3 mr-1" /> Retry failed ({failedScenes.length})
          </Button>
        ) : allUpscaled ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[10px] rounded-full"
            onClick={() => upscaleMutation.mutate({ replace: true })}
            data-testid={`button-upscale-panels-${script.id}`}
          >
            <Sparkles className="h-3 w-3 mr-1" /> Re-run upscaling
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            className="h-6 px-2.5 text-[10px] rounded-full"
            onClick={() => upscaleMutation.mutate(undefined)}
            data-testid={`button-upscale-panels-${script.id}`}
          >
            <Sparkles className="h-3 w-3 mr-1" /> Start panel upscaling
          </Button>
        )}
      </div>
      {anyFailed && !upscaleBusy && (
        <p className="text-[10px] leading-snug text-red-600 dark:text-red-500 mb-1.5" data-testid={`text-panels-upscale-failed-${script.id}`}>
          {failedScenes.length === 1
            ? `Panel ${failedScenes[0]} failed to upscale.`
            : `Panels ${failedScenes.join(", ")} failed to upscale.`}{" "}
          Retry just {failedScenes.length === 1 ? "it" : "those"} above, or tap a failed panel below.
        </p>
      )}
      {!anyUpscaled && !anyFailed && !upscaleBusy && (
        <p className="text-[10px] leading-snug text-amber-600 dark:text-amber-500 mb-1.5" data-testid={`text-panels-need-upscale-${script.id}`}>
          Panels sliced — not yet upscaled. Start upscaling to enhance each frame to 4K.
        </p>
      )}
      {upscaleBusy && (
        <p className="text-[10px] leading-snug text-muted-foreground mb-1.5" data-testid={`text-panels-upscaling-progress-${script.id}`}>
          {upscalingCount > 0
            ? `Upscaling ${upscalingCount} of ${panels.length} panel${panels.length === 1 ? "" : "s"} to 4K — the rest are queued.`
            : "Preparing to upscale panels to 4K…"}
        </p>
      )}
      <div className="grid grid-cols-3 gap-1.5 max-w-[300px]">
        {panels.map((panel) => {
          const n = panel.scene_number || 0;
          const instruction = edits[n] || "";
          // Panel-edit errors live on the panel itself; fall back to any scene
          // video error for panels that already have a clip.
          const sceneErr = panel.edit_error || videoByScene.get(n)?.error;
          const submitting = editMutation.isPending && editMutation.variables?.n === n;
          // Working until the regenerated image swaps in; an error clears it.
          const isWorking = !sceneErr && working[n] !== undefined && working[n] === panel.image_url;
          // Server-driven regeneration (e.g. the automatic post-extraction panel
          // regen) marks the panel edit_status="pending" until its new frame
          // swaps in — surface a spinner for that too.
          const serverBusy = !sceneErr && panel.edit_status === "pending";
          // Per-panel upscale progress, shown distinctly from edits: "processing"
          // is actively upscaling right now; "queued" is waiting its turn.
          const upscaling = panel.upscale_status === "processing";
          const queued = panel.upscale_status === "queued";
          const upscaleFailed = panel.upscale_status === "error";
          const busy = submitting || isWorking || serverBusy || upscaling;

          return (
            <Dialog key={n}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="relative block w-full overflow-hidden rounded-md border border-border/40 hover:border-violet-500/40 transition-colors"
                  data-testid={`button-panel-${n}-${script.id}`}
                >
                  <img
                    src={panel.image_url}
                    alt={`Panel ${n}`}
                    loading="lazy"
                    className="w-full aspect-[9/16] object-cover"
                    data-testid={`img-storyboard-panel-${n}-${script.id}`}
                  />
                  <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[9px] font-semibold leading-none">
                    {n}
                  </span>
                  {upscaling ? (
                    <span
                      className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 text-white"
                      data-testid={`status-panel-upscaling-${n}-${script.id}`}
                    >
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-[8px] font-semibold uppercase tracking-wide">Upscaling</span>
                    </span>
                  ) : queued ? (
                    <span
                      className="absolute inset-0 flex items-center justify-center bg-black/45 text-white/90 text-[8px] font-semibold uppercase tracking-wide"
                      data-testid={`status-panel-queued-${n}-${script.id}`}
                    >
                      Queued
                    </span>
                  ) : upscaleFailed ? (
                    <span
                      className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-red-900/65 text-white"
                      data-testid={`status-panel-upscale-failed-${n}-${script.id}`}
                    >
                      <span className="text-[8px] font-semibold uppercase tracking-wide">Upscale failed</span>
                      <span className="text-[7px] opacity-80">tap to retry</span>
                    </span>
                  ) : busy ? (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Loader2 className="h-4 w-4 animate-spin text-white" />
                    </span>
                  ) : null}
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-[95vw] sm:max-w-md w-fit p-3">
                <DialogHeader>
                  <DialogTitle className="text-sm">Panel {n}</DialogTitle>
                </DialogHeader>
                <div className="relative mx-auto">
                  <img
                    src={panel.image_url}
                    alt={`Panel ${n}`}
                    className="max-w-full max-h-[60vh] w-auto h-auto object-contain rounded-lg mx-auto"
                    data-testid={`img-storyboard-panel-large-${n}-${script.id}`}
                  />
                  {busy && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/45 rounded-lg text-white">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="text-[11px]">Regenerating panel…</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <p className="text-[9px] uppercase tracking-wider font-semibold text-muted-foreground/50">
                    Edit this panel
                  </p>
                  <Textarea
                    value={instruction}
                    onChange={(e) => setEdits((prev) => ({ ...prev, [n]: e.target.value }))}
                    placeholder="Describe a change (e.g. make the mug blue, reveal the product more)…"
                    className="min-h-[60px] text-[12px] leading-relaxed resize-y"
                    disabled={busy}
                    data-testid={`textarea-panel-edit-${n}-${script.id}`}
                  />
                  <Button
                    size="sm"
                    className="h-8 text-[11px] rounded-full w-full"
                    disabled={!instruction.trim() || busy}
                    onClick={() => {
                      setWorking((prev) => ({ ...prev, [n]: panel.image_url || "" }));
                      editMutation.mutate({ n, instruction: instruction.trim() });
                    }}
                    data-testid={`button-panel-apply-edit-${n}-${script.id}`}
                  >
                    {busy ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Regenerating…</>
                    ) : (
                      <><Wand2 className="h-3.5 w-3.5 mr-1" /> Regenerate Panel</>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-[11px] rounded-full w-full"
                    disabled={busy}
                    onClick={() => {
                      setWorking((prev) => ({ ...prev, [n]: panel.image_url || "" }));
                      editMutation.mutate({ n, mode: "product" });
                    }}
                    data-testid={`button-panel-rerender-product-${n}-${script.id}`}
                  >
                    {busy ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Re-rendering…</>
                    ) : (
                      <><Package className="h-3.5 w-3.5 mr-1" /> Re-render Product</>
                    )}
                  </Button>
                  <p className="text-[10px] leading-snug text-muted-foreground/60">
                    Keeps the panel identical but matches the product to your real product reference photo.
                  </p>
                  {upscaleFailed && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 text-[11px] rounded-full w-full border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                      disabled={busy}
                      onClick={() => upscaleMutation.mutate({ scenes: [n] })}
                      data-testid={`button-panel-retry-upscale-${n}-${script.id}`}
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> Retry upscaling this panel
                    </Button>
                  )}
                  {sceneErr && (
                    <p className="text-[11px] text-red-500" data-testid={`text-panel-edit-error-${n}-${script.id}`}>
                      {sceneErr}
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          );
        })}
      </div>
    </div>
  );
}
